import { isPlainArrayOrObject } from './isPlainArrayOrObject';

/**
 * Shallow detector: does this value (as a single string) contain a
 * template run?
 *
 * Returns `true` only for strings that include a `{{` sequence. Non-string
 * inputs return `false`. This is the cheap test DB-1.3's DataSourceManager
 * uses to decide whether a `dataSources` entry needs interpolation before
 * being fetched — it short-circuits to `false` for every non-string leaf.
 *
 * This is a best-effort detector: a literal `{{` inside a static string
 * will flag as a template. ADR-12 does not define an escape mechanism in
 * v1; if real content ever needs literal `{{`, add escaping before relying
 * on this check.
 *
 * @example
 * hasTemplate('Hello {{name}}'); // true
 * hasTemplate('plain string');   // false
 * hasTemplate(42);               // false
 * hasTemplate(null);             // false
 */
export function hasTemplate(value: unknown): boolean {
  return typeof value === 'string' && value.indexOf('{{') !== -1;
}

/**
 * Deep detector: does this value — or any reachable string leaf inside it
 * — contain a template run?
 *
 * Short-circuits on the first hit, so the walk is O(n) in the worst case
 * and typically far less. Non-plain objects (Date, Map, Set, RegExp, class
 * instances, …) are not traversed — matches the same rules as
 * `interpolateObject`. Cycle-safe via a local `WeakSet`.
 *
 * Useful for DB-1.3 "does this prop tree contain any templates?" checks
 * before committing to a full `interpolateObject` walk, and for
 * formState dependency detection.
 *
 * @example
 * hasTemplateDeep({ a: { b: ['plain', '{{x}}'] } }); // true
 * hasTemplateDeep({ a: { b: ['plain'] } });          // false
 */
export function hasTemplateDeep(value: unknown): boolean {
  const stack = new WeakSet<object>();
  return walk(value, stack);
}

function walk(value: unknown, stack: WeakSet<object>): boolean {
  if (hasTemplate(value)) {
    return true;
  }
  if (value === null || typeof value !== 'object') {
    return false;
  }
  if (!isPlainArrayOrObject(value)) {
    return false;
  }
  if (stack.has(value)) {
    return false;
  }
  stack.add(value);
  try {
    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        if (walk(value[i], stack)) {
          return true;
        }
      }
      return false;
    }
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj);
    for (let i = 0; i < keys.length; i++) {
      if (walk(obj[keys[i]], stack)) {
        return true;
      }
    }
    return false;
  } finally {
    stack.delete(value);
  }
}
